import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { UserService } from 'src/app/services/user/user.service';
import { RoleService } from 'src/app/services/role/role.service';
import { UserModalService } from 'src/app/services/user-modal/user-modal.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { CompanyAgentService } from 'src/app/services/company-agent/company-agent.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { User } from 'src/app/shared/models';
import Swal from 'sweetalert2';

declare var $: any;
declare var bootstrap: any;

@Component({
  selector: 'app-update-user-detail-modal',
  templateUrl: './update-user-detail-modal.component.html',
  styleUrls: ['./update-user-detail-modal.component.scss']
})
export class UpdateUserDetailModalComponent extends DestructorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('repeaterBasic', { static: false }) repeaterBasic?: ElementRef;
  @Output() userUpdated = new EventEmitter<void>();

  countryList: any[] = [];
  userForm!: FormGroup;
  isLoading: boolean = false;
  rolesList: any[] = [];
  primaryAgentOptions: any[] = [];
  companyAgentOptions: any[] = [];
  selectedImageFile: File | null = null;
  imagePreviewUrl: string | null = null;
  isImageUploading: boolean = false;
  uploadedImageUrl: string = '';
  selectedUser: User | null = null;

  constructor(
    private fb: FormBuilder,
    private _userService: UserService,
    private _roleService: RoleService,
    private _userModalService: UserModalService,
    private _globalStateService: GlobalStateService,
    private _companyAgentService: CompanyAgentService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    console.log('UpdateUserDetailModalComponent initialized');
    this.getRoles();
    this.getPrimaryAgents();
    this.getCompanyAgents();
    this.setupEditUserListener();
    this.subscribeToUserModalService();

    // Subscribe to countries from GlobalStateService
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countryList = countries || [];
      })
    );
  }

  ngAfterViewInit(): void {
    this.setupModalListener();
    if (this.repeaterBasic?.nativeElement) {
      ($(this.repeaterBasic.nativeElement) as any).repeater({
        initEmpty: false,
        defaultValues: {
          'text-input': 'foo',
        },
        show: function (this: HTMLElement) {
          $(this).slideDown();
        },
        hide: function (this: HTMLElement, deleteElement: () => void) {
          $(this).slideUp(deleteElement);
        },
      });
    }
  }

  private setupEditUserListener(): void {
    console.log('Setting up edit user listener');
    // Remove any existing listener first
    document.removeEventListener('editUser', this.handleEditUser);
    // Add the new listener
    document.addEventListener('editUser', this.handleEditUser);
  }

  private handleEditUser = (event: any) => {
    console.log('Edit user event received in modal component:', event.detail);
    this.selectedUser = event.detail;
    if(this.selectedUser) {
      console.log('Populating form with user:', this.selectedUser);
      this.populateForm(this.selectedUser);
    } else {
      console.warn('No user data received in event');
    }
  }

  private subscribeToUserModalService(): void {
    console.log('Setting up user modal service subscription');
    this.subscriptions.push(
      this._userModalService.selectedUser$.subscribe(user => {
        console.log('User received from service:', user);
        if (user) {
          this.selectedUser = user;
          this.populateForm(user);
        }
      })
    );
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_update_details');
    console.log('Setting up modal listener, modal element:', modalElement);
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        console.log('Modal opened, checking for user data...');
        // First check service
        const serviceUser = this._userModalService.getSelectedUser();
        if (serviceUser) {
          console.log('Using user from service:', serviceUser);
          this.selectedUser = serviceUser;
          this.populateForm(serviceUser);
        } else if (this.selectedUser) {
          console.log('Using stored selected user:', this.selectedUser);
          this.populateForm(this.selectedUser);
        } else {
          console.warn('No user data available');
        }
      });
    } else {
      console.error('Modal element not found during setup');
    }
  }

  initializeForm() {
    this.userForm = this.fb.group({
      avatar: [''],
      title: ['Mr.', [Validators.required]],
      name: ['', [Validators.required]],
      prefix: [''],
      login_id: ['', [Validators.required]],
      password: [''],
      confirm_password: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      email_2: ['', [Validators.email]],
      phone_2: [''],
      mobile: [''],
      fax: [''],
      country: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],
      designation: ['', [Validators.required]],
      status: ['', [Validators.required]],
      is_primary_agent: ['', [Validators.required]],
      primary_agent: [''],
      company_agent_id: [''],
      role: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    // Add conditional validator for primary_agent
    this.userForm.get('is_primary_agent')?.valueChanges.subscribe(value => {
      const primaryAgentControl = this.userForm.get('primary_agent');
      if (value === 'no') {
        primaryAgentControl?.setValidators([Validators.required]);
      } else {
        primaryAgentControl?.clearValidators();
        primaryAgentControl?.setValue('');
      }
      primaryAgentControl?.updateValueAndValidity();
    });
  }

  // Getter to check if user is verified
  get isUserVerified(): boolean {
    return this.selectedUser?.is_verified || false;
  }

  getPrimaryAgents() {
    this.subscriptions.push(
      this._userService.GetData("1", "", { paginate: 'false', is_primary_agent: true, role_type: 30 }).subscribe({
        next: (res: any) => {
          if (res.status && res.data) {
            // Handle non-paginated response structure
            const agents = Array.isArray(res.data) ? res.data : res.data.data || [];
            this.primaryAgentOptions = agents.map((agent: any) => ({
              value: agent._id || agent.doc_id || '',
              label: `${agent.title || ''} ${agent.name || agent.login_id || 'Unknown Agent'}`.trim()
            }));
            console.log('Primary agents loaded:', this.primaryAgentOptions);
          } else {
            this.primaryAgentOptions = [];
          }
        },
        error: (error) => {
          console.error('Error fetching primary agents:', error);
          this.primaryAgentOptions = [];
        }
      })
    );
  }

  getCompanyAgents() {
    this.subscriptions.push(
      this._companyAgentService.GetData("1", "", { paginate: false }).subscribe({
        next: (res: any) => {
          if (res.status && res.data) {
            // Handle non-paginated response structure
            const agents = Array.isArray(res.data) ? res.data : res.data.data || [];
            this.companyAgentOptions = agents.map((agent: any) => ({
              value: agent._id || '',
              label: agent.name || 'Unknown Agent'
            }));
            console.log('Company agents loaded:', this.companyAgentOptions);
          } else {
            console.log('Invalid response structure:', res);
            this.companyAgentOptions = [];
          }
        },
        error: (error) => {
          console.error('Error fetching company agents:', error);
          this.companyAgentOptions = [];
        }
      })
    );
  }

  // Password match validator
  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirm_password');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
    } else if (confirmPassword && confirmPassword.hasError('passwordMismatch')) {
      confirmPassword.setErrors(null);
    }

    return null;
  }

  // Helper method to mark all form fields as touched
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Helper method to get form validation errors
  private getFormValidationErrors(): string {
    const errors: string[] = [];

    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      if (control && control.invalid && control.touched) {
        const fieldName = this.getFieldDisplayName(key);

        if (control.errors?.['required']) {
          errors.push(`${fieldName} is required`);
        }
        if (control.errors?.['email']) {
          errors.push(`${fieldName} must be a valid email address`);
        }
        if (control.errors?.['passwordMismatch']) {
          errors.push(`Password confirmation does not match`);
        }
      }
    });

    return errors.length > 0 ? errors.map(error => `• ${error}`).join('<br>') : 'Please fill in all required fields correctly.';
  }

  // Helper method to get user-friendly field names
  private getFieldDisplayName(fieldName: string): string {
    const fieldNames: { [key: string]: string } = {
      'title': 'Title',
      'name': 'Name',
      'login_id': 'Login ID',
      'email': 'Email',
      'phone': 'Phone',
      'email_2': 'Email 2',
      'designation': 'Designation',
      'status': 'Status',
      'role': 'Role'
    };

    return fieldNames[fieldName] || fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  }

  private populateForm(user: User): void {
    if (!user) return;

    console.log('Populating form with user data:', user);

    // Clear any existing image preview and upload state first
    this.imagePreviewUrl = null;
    this.uploadedImageUrl = '';
    this.isImageUploading = false;

    this.userForm.patchValue({
      title: user.title || 'Mr.',
      name: user.name || '',
      prefix: user.prefix || '',
      login_id: user.login_id || user.email,
      email: user.email || '',
      phone: user.phone || '',
      email_2: user.email_2 || '',
      phone_2: user.phone_2 || '',
      mobile: user.mobile || '',
      fax: user.fax || '',
      country: user.country || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      zip: user.zip || '',
      designation: user.designation || '',
      status: user.status || '',
      is_primary_agent: (user as any).is_primary_agent ? 'yes' : 'no',
      primary_agent: (user as any).primary_agent || '',
      company_agent_id: (user as any).company_agent_id || '',
      role: user.role || ''
    });

    // Handle email field disabling based on verification status
    if (user.is_verified) {
      this.userForm.get('email')?.disable();
      this.userForm.get('email_2')?.disable();
      console.log('Email fields disabled - user is verified');
    } else {
      this.userForm.get('email')?.enable();
      this.userForm.get('email_2')?.enable();
      console.log('Email fields enabled - user is not verified');
    }

    // Handle avatar
    if (user.avatar) {
      this.imagePreviewUrl = user.avatar;
      this.updateImagePreview(user.avatar);
    }

    console.log('Form populated with values:', this.userForm.value);
  }

  getRoles(): void {
    this.subscriptions.push(
      this._roleService.GetData("1").subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.rolesList = response.data.data || [];
          }
        },
        error: (error) => {
          console.error('Error loading roles:', error);
        }
      })
    );
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          text: 'Please select a valid image file (PNG, JPG, JPEG)',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        Swal.fire({
          text: 'File size must be less than 5MB',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      this.selectedImageFile = file;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreviewUrl = e.target.result;
        if (this.imagePreviewUrl) {
          this.updateImagePreview(this.imagePreviewUrl);
        }
      };
      reader.readAsDataURL(file);

      this.userForm.patchValue({ avatar: file.name });

      // Upload the image immediately
      this.uploadImage(file);
    }
  }

  // Upload image to S3
  private uploadImage(file: File): void {
    this.isImageUploading = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
      next: (res) => {
        const imageUrl = res.data.url;
        const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

        this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
          next: () => {
            this.isImageUploading = false;
            this.uploadedImageUrl = imageUrl;
            console.log('Image uploaded successfully:', imageUrl);
          },
          error: (error) => {
            this.isImageUploading = false;
            console.error('Error uploading image to S3:', error);
            Swal.fire({
              text: "Error uploading image. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        });
      },
      error: (error) => {
        this.isImageUploading = false;
        console.error('Error getting presigned URL:', error);
        Swal.fire({
          text: "Error uploading image. Please try again.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    });
  }

  private updateImagePreview(imageUrl: string): void {
    const imageWrapper = document.querySelector('.image-input-wrapper') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = `url(${imageUrl})`;
    }
  }

  onSubmit() {
    // Check if image is still uploading
    if (this.isImageUploading) {
      Swal.fire({
        text: "Please wait for the image to finish uploading.",
        icon: "warning",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    // Set loading state immediately when button is clicked
    this.isLoading = true;

    // Check if form is invalid and show validation errors
    if (this.userForm.invalid) {
      this.markFormGroupTouched(this.userForm);
      const errors = this.getFormValidationErrors();

      // Reset loading state since we're not proceeding with submission
      this.isLoading = false;

      Swal.fire({
        title: "Form Validation Errors",
        html: errors,
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    if (this.selectedUser) {
      // isLoading is already set to true above

      const formValue = this.userForm.value;
      const selectedRole = this.rolesList.find(role => role._id === formValue.role);

      const submitData = {
        title: formValue.title,
        name: formValue.name,
        prefix: formValue.prefix,
        login_id: formValue.login_id,
        email: formValue.email,
        phone: formValue.phone,
        email_2: formValue.email_2,
        phone_2: formValue.phone_2,
        mobile: formValue.mobile,
        fax: formValue.fax,
        country: formValue.country,
        address: formValue.address,
        city: formValue.city,
        state: formValue.state,
        zip: formValue.zip,
        designation: formValue.designation,
        status: formValue.status,
        is_primary_agent: formValue.is_primary_agent === 'yes',
        primary_agent: formValue.is_primary_agent === 'no' ? formValue.primary_agent : null,
        company_agent_id: formValue.company_agent_id,
        role: formValue.role,
        password:"",
        role_name: selectedRole?.name || 'Unknown Role',
        avatar: this.uploadedImageUrl || this.selectedUser.avatar
      };

      // Only include password if it's provided
      if (formValue.password && formValue.password.trim() !== '') {
        submitData.password = formValue.password;
      }

      console.log('Prepared API Data:', submitData);
      console.log('Uploaded Image URL:', this.uploadedImageUrl);

      const userId = this.selectedUser._id || this.selectedUser.doc_id || "";

      this.subscriptions.push(
        this._userService.UpdateData(userId, submitData).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('User updated successfully:', response);

            Swal.fire({
              text: "Agent has been successfully updated!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                this.closeModal();
                this.userUpdated.emit();
                this.resetForm();
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error updating user:', error);
            Swal.fire({
              text: "Sorry, looks like there are some errors detected, please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    }
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_update_details');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
    this.resetForm();
    this._userModalService.clearSelectedUser();
  }

  resetForm(): void {
    this.userForm.reset();
    this.userForm.patchValue({
      title: 'Mr.'
    });
    this.selectedImageFile = null;
    this.imagePreviewUrl = null;
    this.uploadedImageUrl = '';
    this.isImageUploading = false;
    this.selectedUser = null;

    const imageWrapper = document.querySelector('.image-input-wrapper') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = '';
    }
  }

  ngOnDestroy(): void {
    // Remove event listener to prevent memory leaks
    document.removeEventListener('editUser', this.handleEditUser);

    super.ngOnDestroy();
  }
}
